import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import {
  Grid,
  Button,
  Typography,
  Box,
  Link,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import GreatDatePicker from '../ui/forms/fields/datePicker';
import Input from '../ui/forms/fields/input';
import DeleteIcon from '@mui/icons-material/Delete';
import PrimaryButton from '../ui/buttons/primaryButton';
import { useGA4React } from 'ga-4-react';
import { Report } from '../../libs/loggers';

const validationSchema = yup.object().shape({
  beneficiaries: yup.array().of(
    yup.object().shape({
      title: yup.string().required('Title is required'),
      initials: yup.string().required('Initials is required'),
      name: yup.string().required('Full Name and Surname is required'),
      idNumber: yup.string().required('ID/Passport number is required'),
      dateOfBirth: yup
        .date()
        .required('Date of Birth is required')
        .typeError('Date of Birth is required'),
      contact: yup.string().required('Contact No is required'),
      relationship: yup.string().required('Relationship to Member is required'),
      percentage: yup.string().required('Percentage is required'),
    })
  ),
  stepSlug: yup.string().required(),
  percentageTotal: yup
    .number()
    .required('Beneficiary Percentage should equal to 100%.')
    .typeError('Percentage Total must be a number')
    .min(100, 'Percentage Total must be at least 100')
    .max(100, 'Percentage Total must be at most 100'),
});

const BeneficiariesNominations = ({
  extendedForm,
  data,
  submitionData,
  onNext,
  onBack,
  stepSlug,
  openSave,
  onSaveAndExit,
  isRaHelp,
}) => {
  const { control, handleSubmit, setValue, getValues, formState, ...methods } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      beneficiaries: [
        {
          title: '',
          initials: '',
          name: '',
          idNumber: '',
          dateOfBirth: null,
          contact: '',
          email: '',
          relationship: '',
          percentage: '',
        },
      ],
      percentageTotal: 0,
    },
  });

  let ga4React = useGA4React();
  const loading = useSelector((state) => state.submition.submition?.loading);
  const userId = useSelector((state) => state.submition.data?.userId);
  const page = window.location.pathname.replace(/^\/|\/$/g, '');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const submitionId = useSelector((state) => state.submition.submition?.submitionId);
  const formStepper = useSelector((state) => state.formStepper.value);
  const { watch } = methods;
  const [totalPercentage, setTotalPercentage] = useState(0);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'beneficiaries',
  });

  useEffect(() => {
    setValue('stepSlug', stepSlug, { shouldValidate: true });
  }, [stepSlug, setValue]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      window.scrollTo(0, 0);
    }
  }, []);

  const watchedPercentages = watch();

  useEffect(() => {
    let calculatedTotalPercentage = 0;

    watchedPercentages.beneficiaries.forEach((beneficiary) => {
      calculatedTotalPercentage += parseFloat(beneficiary.percentage || 0);
    });

    setTotalPercentage(calculatedTotalPercentage);
  }, [watchedPercentages]);

  useEffect(() => {
    setValue('percentageTotal', totalPercentage);
  }, [totalPercentage, setValue]);

  useEffect(() => {
    if (submitionData && submitionData.beneficiaries) {
      const numberOfBeneficiaries = submitionData.beneficiaries.length;

      while (fields.length > numberOfBeneficiaries) {
        remove(fields.length - 1);
      }

      for (let index = 0; index < numberOfBeneficiaries; index++) {
        if (fields[index]) {
          setValue(`beneficiaries[${index}].title`, submitionData.beneficiaries[index].title);
          setValue(`beneficiaries[${index}].initials`, submitionData.beneficiaries[index].initials);
          setValue(`beneficiaries[${index}].name`, submitionData.beneficiaries[index].name);
          setValue(`beneficiaries[${index}].idNumber`, submitionData.beneficiaries[index].idNumber);
          setValue(
            `beneficiaries[${index}].dateOfBirth`,
            submitionData.beneficiaries[index].dateOfBirth
          );
          setValue(`beneficiaries[${index}].contact`, submitionData.beneficiaries[index].contact);
          setValue(`beneficiaries[${index}].email`, submitionData.beneficiaries[index].email);
          setValue(
            `beneficiaries[${index}].relationship`,
            submitionData.beneficiaries[index].relationship
          );
          setValue(
            `beneficiaries[${index}].percentage`,
            submitionData.beneficiaries[index].percentage
          );
        } else {
          append({
            title: submitionData.beneficiaries[index].title,
            initials: submitionData.beneficiaries[index].initials,
            name: submitionData.beneficiaries[index].name,
            idNumber: submitionData.beneficiaries[index].idNumber,
            dateOfBirth: submitionData.beneficiaries[index].dateOfBirth,
            contact: submitionData.beneficiaries[index].contact,
            email: submitionData.beneficiaries[index].email,
            relationship: submitionData.beneficiaries[index].relationship,
            percentage: submitionData.beneficiaries[index].percentage,
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    const eventInfo = {
      action: 'view',
      category: 'step4_formstep_view',
      description: 'viewed a form step in step 4',
      value: {
        currentPage: page,
        stepSlug,
        activeFormStep: formStepper?.step,
      },
    };

    Report(eventInfo, userId, submitionId);
  }, []);

  const NavButtons = (props) => (
    <>
      <Box display="flex" justifyContent={'flex-end'}>
        <PrimaryButton disabled={props.isFirstStep} onClick={() => onBack()} sx={{ mt: 1, mr: 1 }}>
          Back
        </PrimaryButton>
        <PrimaryButton
          onClick={() => handleNext()}
          disabled={loading}
          sx={{
            mt: 1,
            mr: 1,
            '&.Mui-disabled': {
              background: '#CC6323',
              color: '#fff',
            },
          }}
        >
          {userId ? 'Save & Continue' : 'Continue'}{' '}
          {loading && <CircularProgress sx={{ ml: 1 }} size={isMobile ? 15 : 20} color="inherit" />}
        </PrimaryButton>
        <PrimaryButton variant="outlined" onClick={() => handleSaveAndExit(userId)} sx={{ mt: 1 }}>
          {'Save & Exit'}
        </PrimaryButton>
      </Box>
      {!userId && (
        <Box display="flex" justifyContent={'flex-end'} marginTop={1}>
          <Typography variant="body1">
            {' '}
            Your progress is not being saved. Click{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              variant="body1"
              underline="always"
              onClick={() => {
                handleOpenSave();
              }}
            >
              here
            </Link>{' '}
            to save.
          </Typography>
        </Box>
      )}
    </>
  );

  const maxBeneficiaries = isRaHelp ? 6 : 4;

  const handleAddBeneficiary = () => {
    if (fields.length < maxBeneficiaries) {
      append({
        title: '',
        initials: '',
        name: '',
        idNumber: '',
        dateOfBirth: null,
        contact: '',
        email: '',
        relationship: '',
        percentage: '',
      });
    } else {
      console.log('Maximum number of beneficiaries reached.');
    }
  };

  const handleNext = async () => {
    ga4React.event('complete_form_step', `${stepSlug}`, 'investement_submition');
    setValue('completeProcess', false, { shouldValidate: true });
    handleSubmit(onSubmit)();
  };

  const handleSaveAndExit = (userId) => {
    const data = getValues();
    const newSubmition = { ...submitionData, ...data };
    onSaveAndExit(newSubmition, userId);
  };

  const handleOpenSave = () => {
    const data = getValues();
    const newSubmition = { ...submitionData, ...data };
    openSave(newSubmition);
  };

  const onSubmit = (data) => {
    const newSubmition = { ...submitionData, ...data };
    onNext(newSubmition);
  };

  return (
    <form noValidate>
      <Grid container spacing={1}>
        {fields.map((item, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Typography variant="h5" color="primary" mt={2}>{`Beneficiary ${
              index + 1
            }`}</Typography>
            <Input
              required
              label="Title"
              defaultValue={item.title}
              {...methods.register(`beneficiaries[${index}].title`)}
              error={!!formState.errors?.beneficiaries?.[index]?.title}
              helperText={formState.errors?.beneficiaries?.[index]?.title?.message}
            />
            <Input
              required
              label="Initials"
              defaultValue={item.initials}
              {...methods.register(`beneficiaries[${index}].initials`)}
              error={!!formState.errors?.beneficiaries?.[index]?.initials}
              helperText={formState.errors?.beneficiaries?.[index]?.initials?.message}
            />
            <Input
              required
              label="Full Name and Surname"
              defaultValue={item.name}
              {...methods.register(`beneficiaries[${index}].name`)}
              error={!!formState.errors?.beneficiaries?.[index]?.name}
              helperText={formState.errors?.beneficiaries?.[index]?.name?.message}
            />
            <Input
              required
              label="ID/Passport number (if foreign national)"
              defaultValue={item.idNumber}
              {...methods.register(`beneficiaries[${index}].idNumber`)}
              error={!!formState.errors?.beneficiaries?.[index]?.idNumber}
              helperText={formState.errors?.beneficiaries?.[index]?.idNumber?.message}
            />

            <GreatDatePicker
              name={`beneficiaries[${index}].dateOfBirth`}
              control={control}
              label="Date Of Birth"
              defaultValue={item.dateOfBirth}
              error={!!formState.errors?.beneficiaries?.[index]?.dateOfBirth}
              helperText={formState.errors?.beneficiaries?.[index]?.dateOfBirth?.message}
              openTo={'year'}
            />
            <Input
              required
              label="Contact No"
              defaultValue={item.contact}
              {...methods.register(`beneficiaries[${index}].contact`)}
              error={!!formState.errors?.beneficiaries?.[index]?.contact}
              helperText={formState.errors?.beneficiaries?.[index]?.contact?.message}
            />
            <Input
              required
              label="Email"
              defaultValue={item.contact}
              {...methods.register(`beneficiaries[${index}].email`)}
              error={!!formState.errors?.beneficiaries?.[index]?.email}
              helperText={formState.errors?.beneficiaries?.[index]?.email?.message}
            />
            <Input
              required
              label="Relationship to Member"
              defaultValue={item.relationship}
              {...methods.register(`beneficiaries[${index}].relationship`)}
              error={!!formState.errors?.beneficiaries?.[index]?.relationship}
              helperText={formState.errors?.beneficiaries?.[index]?.relationship?.message}
            />
            <Input
              type="number"
              required
              label="Percentage(%)"
              defaultValue={item.percentage}
              {...methods.register(`beneficiaries[${index}].percentage`)}
              error={!!formState.errors?.beneficiaries?.[index]?.percentage}
              helperText={formState.errors?.beneficiaries?.[index]?.percentage?.message}
            />

            {index > 0 && (
              <Button onClick={() => remove(index)} variant="outlined" startIcon={<DeleteIcon />}>
                Delete
              </Button>
            )}
          </Grid>
        ))}
      </Grid>
      <Input
        required
        label="Percentage Total"
        sx={{ mt: 2 }}
        {...methods.register('percentageTotal')}
        error={!!formState.errors?.percentageTotal}
        helperText={formState.errors?.percentageTotal?.message}
      />
      <PrimaryButton onClick={handleAddBeneficiary} disabled={fields.length >= maxBeneficiaries}>
        Add Beneficiary
      </PrimaryButton>

      <Grid item xs={12} md={12}>
        <NavButtons isLastStep={false} isFirstStep={false} index={0} />
      </Grid>
    </form>
  );
};

export default BeneficiariesNominations;
